import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'
import { useApi } from '@/utils/axios'
import { AVALIACAO_ROTAS } from '@/utils/apiRoutes'

export const useAvaliacaoStore = defineStore('avaliacao', () => {
  // state
  const items = ref([])
  const meta = reactive({
    search: null,
    current_page: 1,
    total: 0,
    per_page: 100,
    last_page: 1,
  })
  const loading = ref(false)
  const error = ref(null)

  // filtros padrão
  const filters = reactive({
    notes: null,
    searchField: null,
    search: null,
    init_created_at: null,
    end_created_at: null,
    per_page: 100,
    page: 1,
  })

  // computed helpers
  const total = computed(() => meta.total)
  const currentPage = computed(() => meta.current_page)
  const lastPage = computed(() => meta.last_page)
  const perPage = computed(() => meta.per_page)

  // fetch com parâmetros (usa filtros reativos por padrão)
  async function fetchAvaliacoes(options = {}) {
    loading.value = true
    error.value = null

    const endHours = 'T23:59:59'

    const endVal =
      options.end_created_at !== undefined ? options.end_created_at : filters.end_created_at
    const end_created_at =
      endVal && typeof endVal === 'string' && !endVal.includes('T')
        ? `${endVal}${endHours}`
        : endVal

    const params = {
      notes: options.notes,
      searchField: options.searchField !== undefined ? options.searchField : filters.searchField,
      search: options.search,
      init_created_at:
        options.init_created_at !== undefined ? options.init_created_at : filters.init_created_at,
      end_created_at,
      per_page: options.per_page !== undefined ? options.per_page : filters.per_page,
      page: options.page !== undefined ? options.page : filters.page,
    }

    // normalize: remove null/undefined/empty params
    Object.keys(params).forEach((k) => {
      if (params[k] === null || params[k] === undefined || params[k] === '') {
        delete params[k]
      }
    })

    try {
      const response = await useApi(AVALIACAO_ROTAS.LISTAR).fetch({ params })
      items.value = Array.isArray(response.data) ? response.data : []
      if (response.meta) {
        meta.search = response.meta.search ?? null
        meta.current_page = response.meta.current_page ?? params.page ?? 1
        meta.total = response.meta.total ?? 0
        meta.per_page = response.meta.per_page ?? params.per_page ?? 100
        meta.last_page = response.meta.last_page ?? 1
      }
      // persistir últimos filtros/pagina usados
      filters.search = params.search ?? null
      filters.init_created_at = params.init_created_at ?? null
      filters.end_created_at = params.end_created_at ?? null
      filters.per_page = params.per_page ?? filters.per_page
      filters.page = params.page ?? filters.page

      return { success: true, data: items.value, meta }
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Erro ao carregar avaliações'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  // utilitários
  function setFilter(partial = {}) {
    Object.assign(filters, partial)
  }

  function setPage(p) {
    filters.page = p
    return fetchAvaliacoes({ page: p })
  }

  function setPerPage(pp) {
    filters.per_page = pp
    filters.page = 1
    return fetchAvaliacoes({ per_page: pp, page: 1 })
  }

  function resetFilters() {
    const now = new Date()

    const dateNow = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      .toISOString()
      .substr(0, 10)

    filters.notes = null
    filters.search = null
    filters.init_created_at = dateNow
    filters.end_created_at = dateNow
    filters.per_page = 100
    filters.page = 1
    fetchAvaliacoes({})
  }

  function clearData() {
    items.value = []
    meta.current_page = 1
    meta.total = 0
    meta.per_page = 100
    meta.last_page = 1
    error.value = null
  }

  async function createResponseIA(id, payload = {}) {
    loading.value = true
    error.value = null
    try {
      const route = AVALIACAO_ROTAS.CREATE_RESPONSE_AI(id)
      const response = await useApi(route).post(payload)
      await fetchAvaliacoes()
      return { success: true, data: response.data ?? response }
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Erro ao criar resposta IA'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function changeStatusAvaliacaoObs(idObs) {
    loading.value = true
    error.value = null
    try {
      const route = AVALIACAO_ROTAS.CHANGE_STATUS_AVALIACAO_OBS(idObs)
      const response = await useApi(route).put({})
      await fetchAvaliacoes()
      return { success: true, data: response.data ?? response }
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Erro ao criar resposta IA'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function resolveAllAvaliacaoObs(ids) {
    loading.value = true
    error.value = null
    try {
      const response = await useApi(AVALIACAO_ROTAS.CHANGE_STATUS_ALL_AVALIACAO_OBS).post({ ids })
      f
      return { success: true, data: response.data ?? response }
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Erro ao resolver avaliações'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  return {
    // state
    items,
    meta,
    loading,
    error,
    filters,

    // computed
    total,
    currentPage,
    lastPage,
    perPage,

    // actions
    fetchAvaliacoes,
    setFilter,
    setPage,
    setPerPage,
    resetFilters,
    clearData,
    createResponseIA,
    changeStatusAvaliacaoObs,
    resolveAllAvaliacaoObs,
  }
})
